import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import RobotoRegular from '../fonts/Roboto-Regular.ttf';
import RobotoBold from '../fonts/Roboto-Bold.ttf';
import RobotoItalic from '../fonts/Roboto-Italic.ttf';
import encabezadoA4 from './informes/encabezado_A4.png';
import pieHojaA4 from './informes/pie_hoja_A4.png';

// Mismo criterio que "Informe técnico con observaciones" (InformeTecnicoPDF.jsx):
// TTF estáticos, márgenes laterales de 2 cm alineados con los membretes, texto
// justificado y número de página en el View fijo del pie. Sin recuadros por
// sección: entre secciones solo una línea horizontal.
Font.register({
    family: 'Roboto',
    fonts: [
        { src: RobotoRegular, fontWeight: 400 },
        { src: RobotoBold, fontWeight: 700 },
        { src: RobotoItalic, fontWeight: 400, fontStyle: 'italic' },
    ],
});

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#ffffff',
        fontFamily: 'Roboto',
        fontSize: 10,
        color: '#333333',
        paddingTop: 84,      // deja lugar para el encabezado (imagen fija) + margen
        paddingBottom: 122,  // deja lugar para el pie de página (imagen fija) + margen
        paddingHorizontal: 57,  // margen izquierdo/derecho de 2 cm
    },
    encabezado: {
        position: 'absolute',
        top: 24,
        left: 57,
        right: 57,
    },
    encabezadoImg: {
        width: '100%',
    },
    pie: {
        position: 'absolute',
        bottom: 24,
        left: 57,
        right: 57,
    },
    pieImg: {
        width: '100%',
    },
    tituloPage: {
        fontSize: 16,
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.5,
        marginBottom: 2,
    },
    subtitulo: {
        fontSize: 12,
        fontWeight: 700,
        textAlign: 'center',
        color: '#555555',
        marginBottom: 14,
    },
    grid: {
        flexDirection: 'row',
    },
    col: {
        flex: 1,
    },
    fila: {
        marginBottom: 4,
    },
    filaLabel: {
        fontWeight: 700,
    },
    seccionTitulo: {
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 12,
    },
    separador: {
        borderBottomWidth: 1,
        borderBottomColor: '#d1d5db',
        marginTop: 10,
        marginBottom: 10,
    },
    textoPre: {
        marginBottom: 10,
        textAlign: 'justify',
    },
    fecha: {
        textAlign: 'right',
        marginTop: 16,
        fontSize: 9.5,
        color: '#666666',
    },
    numeroPagina: {
        textAlign: 'center',
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
});

// Los beneficiarios siempre pertenecen a la provincia de Buenos Aires: se ocultan
// las menciones de provincia/país al final del domicilio (", Provincia de Buenos
// Aires, Argentina" y variantes) para no repetir información.
export const limpiarDireccion = (val) => {
    if (!val) return '-';
    let s = String(val).trim();
    s = s
        .replace(/,\s*(?:provincia\s+de)?\s*buenos\s+aires\s*,?\s*argentina\s*/gi, '')
        .replace(/,\s*provincia\s+de\s+buenos\s+aires\s*/gi, '')
        .replace(/,\s*(?:buenos\s+aires\s*,?\s*)?argentina\s*/gi, '');
    s = s.replace(/^\s*,\s*/, '').replace(/\s*,\s*$/, '').replace(/\s{2,}/g, ' ').trim();
    return s || '-';
};

// Planilla de No Objeción (Microcréditos) armada como TEXTO (react-pdf):
// flujo y salto de página nativos, nunca corta una línea por la mitad.
const NoObjecionDocumentoPDF = ({ datos }) => {
    const {
        nombre_obra,
        expediente,
        partido_nombre,
        programa_lineamiento,
        area_responsable,
        nombre_beneficiario,
        documento,
        direccion,
        barrio,
        localidad,
        monto,
        fecha_emision,
    } = datos || {};

    const montoStr = monto ? `$${Number(monto).toLocaleString('es-AR')}` : '-';

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.encabezado}>
                    <Image style={styles.encabezadoImg} src={encabezadoA4} />
                </View>

                <Text style={styles.tituloPage}>Planilla de No Objeción</Text>
                <Text style={styles.subtitulo}>Dirección Provincial de Intervenciones Complementarias</Text>

                {/* Sección 1: datos del proyecto */}
                <View style={styles.grid}>
                    <View style={styles.col}>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Proyecto: </Text>
                            {nombre_obra || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Expediente: </Text>
                            {expediente || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Partido: </Text>
                            {partido_nombre || '-'}
                        </Text>
                    </View>
                    <View style={styles.col}>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Programa: </Text>
                            {programa_lineamiento || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Área Responsable: </Text>
                            {area_responsable || '-'}
                        </Text>
                    </View>
                </View>

                <View style={styles.separador} />

                {/* Sección 2: datos del beneficiario */}
                <Text style={styles.seccionTitulo}>Datos del Beneficiario</Text>
                <View style={styles.grid}>
                    <View style={styles.col}>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Nombre: </Text>
                            {nombre_beneficiario || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Documento: </Text>
                            {documento || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Domicilio: </Text>
                            {limpiarDireccion(direccion)}
                        </Text>
                    </View>
                    <View style={styles.col}>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Barrio: </Text>
                            {barrio || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Localidad: </Text>
                            {localidad || '-'}
                        </Text>
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Monto: </Text>
                            {montoStr}
                        </Text>
                    </View>
                </View>

                <View style={styles.separador} />

                {/* Sección 3: texto de la constancia */}
                <Text style={styles.textoPre}>
                    Por medio de la presente, se deja constancia que no existen objeciones técnicas ni
                    administrativas para que el/la beneficiario/a{' '}
                    <Text style={styles.filaLabel}>{nombre_beneficiario || '-'}</Text> acceda al subsidio
                    correspondiente en el marco del proyecto{' '}
                    <Text style={styles.filaLabel}>{nombre_obra || '-'}</Text>.
                </Text>
                <Text style={styles.textoPre}>
                    Se otorga la presente No Objeción a los efectos de continuar con el trámite administrativo
                    correspondiente.
                </Text>

                <Text style={styles.fecha}>Fecha de emisión: {fecha_emision}</Text>

                <View fixed style={styles.pie}>
                    <Image style={styles.pieImg} src={pieHojaA4} />
                    <Text render={({ pageNumber }) => `-${pageNumber}-`} style={styles.numeroPagina} />
                </View>
            </Page>
        </Document>
    );
};

export default NoObjecionDocumentoPDF;